import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// Utilitários para formatação
function formatDate(date: string | Date | undefined): string {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('pt-BR');
}

function formatCurrency(value: number | undefined): string {
  if (!value) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatCpfCnpj(value: string | null | undefined): string {
  if (!value) return 'N/A';
  const clean = value.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (clean.length === 14) {
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

interface InvoiceEvent {
  eventId: string;
  eventTitle: string;
  eventSlug: string;
  endDate: Date;
  organizer: {
    name: string;
    cpfCnpj: string | null;
    personType: 'fisica' | 'juridica' | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
  };
  totalAmountPaid: number;
  totalRegistrations: number;
  feePercentage: number;
  parishFeeAmount: number;
  invoiceIssued: boolean;
  invoicePaid: boolean;
  invoiceNumber: string | null;
  invoiceDate: Date | null;
  paymentDate: Date | null;
  paymentMethod: string | null;
}

const InvoiceIssuancePage: React.FC = () => {
  // Obter mês atual no formato YYYY-MM
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    eventId: '',
    month: getCurrentMonth(), // Inicia com o mês atual
  });
  const [events, setEvents] = useState<InvoiceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<InvoiceEvent | null>(null);
  const [markPaidDialog, setMarkPaidDialog] = useState(false);
  const [registerInvoiceDialog, setRegisterInvoiceDialog] = useState(false);
  const [markPaidData, setMarkPaidData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    notes: '',
  });
  const [registerInvoiceData, setRegisterInvoiceData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch events for invoice
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/invoices/events', { params: filters });
      if (response.data.success) {
        setEvents(response.data.data.events || []);
      }
    } catch (error: any) {
      console.error('Erro ao buscar eventos:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao carregar dados de emissão de notas.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [filters.eventId, filters.month]);

  const handleMarkAsPaid = (event: InvoiceEvent) => {
    setSelectedEvent(event);
    setMarkPaidDialog(true);
  };

  const handleRegisterInvoice = (event: InvoiceEvent) => {
    setSelectedEvent(event);
    setRegisterInvoiceDialog(true);
  };

  const handleSubmitMarkAsPaid = async () => {
    if (!selectedEvent) return;
    try {
      setSubmitting(true);
      await api.put(`/dashboard/invoices/events/${selectedEvent.eventId}/mark-paid`, markPaidData);
      setSnackbar({ open: true, message: 'Nota marcada como paga com sucesso.', severity: 'success' });
      setMarkPaidDialog(false);
      setSelectedEvent(null);
      setMarkPaidData({
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        notes: '',
      });
      fetchEvents();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao marcar nota como paga.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRegisterInvoice = async () => {
    if (!selectedEvent) return;
    try {
      setSubmitting(true);
      await api.post(`/dashboard/invoices/events/${selectedEvent.eventId}/register`, registerInvoiceData);
      setSnackbar({ open: true, message: 'Emissão de nota registrada com sucesso.', severity: 'success' });
      setRegisterInvoiceDialog(false);
      setSelectedEvent(null);
      setRegisterInvoiceData({
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchEvents();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao registrar emissão de nota.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Emissão de Notas
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Eventos finalizados - Emissão de notas fiscais sobre a taxa de {events[0]?.feePercentage || 6.9}% do valor arrecadado
      </Typography>

      {/* Filtros */}
      <Card sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="ID do Evento"
              value={filters.eventId}
              onChange={(e) => setFilters({ ...filters, eventId: e.target.value })}
              size="small"
            />
            <TextField
              label="Mês"
              type="month"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <Button variant="contained" onClick={fetchEvents}>
              Buscar
            </Button>
            {filters.month && (
              <Button 
                variant="outlined" 
                onClick={() => setFilters({ ...filters, month: '' })}
              >
                Limpar Filtro de Mês
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Lista de Eventos */}
      {events.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              Nenhum evento encontrado para emissão de notas.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Evento</TableCell>
              <TableCell>Organizador</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Valor Arrecadado</TableCell>
              <TableCell>Taxa ({events[0]?.feePercentage || 6.9}%)</TableCell>
              <TableCell>Valor da Nota</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.eventId}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {event.eventTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Finalizado em {formatDate(event.endDate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {event.totalRegistrations} inscrição(ões)
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{event.organizer.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatCpfCnpj(event.organizer.cpfCnpj)}
                  </Typography>
                  {event.organizer.email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {event.organizer.email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={event.organizer.personType === 'fisica' ? 'Pessoa Física' : event.organizer.personType === 'juridica' ? 'Pessoa Jurídica' : 'N/A'}
                    size="small"
                    color={event.organizer.personType === 'fisica' ? 'primary' : 'secondary'}
                  />
                </TableCell>
                <TableCell>{formatCurrency(event.totalAmountPaid)}</TableCell>
                <TableCell>{event.feePercentage}%</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {formatCurrency(event.parishFeeAmount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {event.invoicePaid ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Paga"
                      size="small"
                      color="success"
                    />
                  ) : event.invoiceIssued ? (
                    <Chip
                      label="Emitida"
                      size="small"
                      color="info"
                    />
                  ) : (
                    <Chip
                      label="Pendente"
                      size="small"
                      color="warning"
                    />
                  )}
                  {event.invoiceNumber && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      NF: {event.invoiceNumber}
                    </Typography>
                  )}
                  {event.paymentDate && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Pago em {formatDate(event.paymentDate)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    {!event.invoiceIssued && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        onClick={() => handleRegisterInvoice(event)}
                      >
                        Registrar Emissão
                      </Button>
                    )}
                    {event.invoiceIssued && !event.invoicePaid && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<PaymentIcon />}
                        onClick={() => handleMarkAsPaid(event)}
                      >
                        Marcar como Paga
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Dialog para registrar emissão de nota */}
      <Dialog open={registerInvoiceDialog} onClose={() => setRegisterInvoiceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Emissão de Nota Fiscal</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Evento: {selectedEvent.eventTitle}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Organizador: {selectedEvent.organizer.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Valor da Nota: <strong>{formatCurrency(selectedEvent.parishFeeAmount)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Número da Nota Fiscal"
                    fullWidth
                    value={registerInvoiceData.invoiceNumber}
                    onChange={(e) => setRegisterInvoiceData({ ...registerInvoiceData, invoiceNumber: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Data de Emissão"
                    type="date"
                    fullWidth
                    value={registerInvoiceData.invoiceDate}
                    onChange={(e) => setRegisterInvoiceData({ ...registerInvoiceData, invoiceDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observações"
                    fullWidth
                    multiline
                    rows={3}
                    value={registerInvoiceData.notes}
                    onChange={(e) => setRegisterInvoiceData({ ...registerInvoiceData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterInvoiceDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmitRegisterInvoice} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para marcar como paga */}
      <Dialog open={markPaidDialog} onClose={() => setMarkPaidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Marcar Nota como Paga</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Evento: {selectedEvent.eventTitle}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Valor: <strong>{formatCurrency(selectedEvent.parishFeeAmount)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Data do Pagamento"
                    type="date"
                    fullWidth
                    value={markPaidData.paymentDate}
                    onChange={(e) => setMarkPaidData({ ...markPaidData, paymentDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Método de Pagamento</InputLabel>
                    <Select
                      value={markPaidData.paymentMethod}
                      label="Método de Pagamento"
                      onChange={(e) => setMarkPaidData({ ...markPaidData, paymentMethod: e.target.value })}
                    >
                      <MenuItem value="pix">PIX</MenuItem>
                      <MenuItem value="credit_card">Cartão de Crédito</MenuItem>
                      <MenuItem value="debit_card">Cartão de Débito</MenuItem>
                      <MenuItem value="boleto">Boleto</MenuItem>
                      <MenuItem value="transfer">Transferência Bancária</MenuItem>
                      <MenuItem value="cash">Dinheiro</MenuItem>
                      <MenuItem value="other">Outro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Observações"
                    fullWidth
                    multiline
                    rows={3}
                    value={markPaidData.notes}
                    onChange={(e) => setMarkPaidData({ ...markPaidData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmitMarkAsPaid} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceIssuancePage;

